/**
 * Distribution Calculator - Shared calculation logic for TRONC distribution
 * This file mirrors the backend DistributionCalculator.cs logic
 * Simplified to 3 core strategies with universal options.
 */

// Distribution types matching backend enum
export const DistributionType = {
  Equal: 0,
  RoleScore: 1,
  ByDepartment: 2,
  // Legacy aliases
  ByJobRole: 1,
  ByJobRoleScore: 1,
  WeightedHours: 1, // legacy — treated as RoleScore
};

// Legacy type mappings for backward compatibility
const LEGACY_TYPE_MAP = {
  3: 1, // Old WeightedHours -> RoleScore
  4: 0, // Old ByHoursAndJobRole -> Equal
  5: 0, // Old HoursWeighted -> Equal
  6: 0, // Old RolePool -> Equal
  7: 0, // Old MinimumFloor -> Equal
  8: 0, // Old SeniorityWeighted -> Equal
};

// Tolerance for percentage sum validation
export const PERCENTAGE_TOLERANCE = 0.01;
export const REQUIRED_TOTAL_PERCENTAGE = 100;

// ============================================================================
// EQUAL DISTRIBUTION STRATEGY
// ============================================================================

const EqualDistributionStrategy = {
  type: DistributionType.Equal,

  getDescription: () =>
    'Distributes TRONC equally among all eligible employees regardless of role or hours worked.',

  validate: (rule) => {
    const errors = [];
    if (!rule.name || rule.name.trim() === '') {
      errors.push('Rule name is required.');
    }
    return { isValid: errors.length === 0, errors };
  },

  calculate: (context) => {
    const { troncAmount, employees } = context;
    if (!employees || employees.length === 0) return [];

    const distributions = [];
    const equalAmount = Math.round((troncAmount / employees.length) * 100) / 100;
    let remaining = troncAmount;

    employees.forEach((emp, index) => {
      let amount;
      if (index === employees.length - 1) {
        amount = Math.round(remaining * 100) / 100;
      } else {
        amount = equalAmount;
        remaining -= amount;
      }

      distributions.push({
        employeeId: emp.id,
        employeeName: emp.name || emp.fullName || `${emp.firstName} ${emp.lastName}`,
        jobRoleName: emp.jobRoleName || emp.role || emp.jobTitle,
        amount,
        percentage: troncAmount > 0 ? (amount / troncAmount) * 100 : 0,
        calculation: { rolePercentage: 0, points: 0, weightedPoints: 0 }
      });
    });

    return distributions;
  }
};

// ============================================================================
// ROLE SCORE DISTRIBUTION STRATEGY
// ============================================================================

const RoleScoreDistributionStrategy = {
  type: DistributionType.RoleScore,

  getDescription: () =>
    'Distributes TRONC based on each job role\'s configured score multiplier. ' +
    'Each role\'s share is proportional to its multiplier relative to the total. ' +
    'Within each role, the allocated amount is split equally among employees.',

  validate: (rule) => {
    const errors = [];
    if (!rule.name || rule.name.trim() === '') {
      errors.push('Rule name is required.');
    }
    
    // For ByJobRole type with allocations, validate them
    const allocations = (rule.allocations || []).filter(a => a.jobRoleId);
    
    if (allocations.length === 0) {
      errors.push('By Job Role distribution requires at least one job role allocation.');
    } else {
      // Check for non-positive multipliers
      const hasNonPositive = allocations.some(a => (a.scoreMultiplier || 0) <= 0);
      if (hasNonPositive) {
        errors.push('Score multipliers must be greater than zero.');
      }
      
      // Check for duplicates
      const roleIds = allocations.map(a => a.jobRoleId);
      const uniqueRoleIds = new Set(roleIds);
      if (roleIds.length !== uniqueRoleIds.size) {
        errors.push('Cannot have duplicate job role allocations.');
      }
    }
    
    return { isValid: errors.length === 0, errors };
  },

  calculate: (context) => {
    const { troncAmount, employees, rule } = context;
    if (!employees || employees.length === 0) return [];

    const allocations = (rule?.allocations || []).filter(a => a.jobRoleId);
    if (allocations.length === 0) {
      // No allocations configured — fall back to equal
      return EqualDistributionStrategy.calculate(context);
    }

    const allocationMap = allocations.reduce((acc, a) => ({
      ...acc,
      [a.jobRoleId]: parseFloat(a.scoreMultiplier) || 1.0
    }), {});

    // Assign each employee their role score (0 if not in allocation)
    const getEmployeeRoleScore = (emp) => {
      const roleId = emp.jobRoleId || emp.primaryJobRoleId;
      return allocationMap[roleId] ?? 0;
    };

    return computeEmployeeAmounts(context, getEmployeeRoleScore);
  }
};

// ============================================================================
// BY DEPARTMENT DISTRIBUTION STRATEGY
// ============================================================================

const ByDepartmentDistributionStrategy = {
  type: DistributionType.ByDepartment,

  getDescription: () =>
    'Distributes TRONC based on department score multipliers (FOH, BOH, Admin, Management). ' +
    'Within each department, the allocated amount is split equally among employees. ' +
    'Each department\'s share is proportional to its multiplier.',

  validate: (rule) => {
    const errors = [];

if (!rule.name || rule.name.trim() === '') {
      errors.push('Rule name is required.');
    }

    const deptAllocations = (rule.allocations || []).filter(a => a.department);
    
    if (deptAllocations.length === 0) {
    errors.push('ByDepartment distribution requires at least one department allocation.');
      return { isValid: false, errors };
    }

    // Check for non-positive multipliers
    const hasNonPositive = deptAllocations.some(a => (a.scoreMultiplier || 0) <= 0);
    if (hasNonPositive) {
      errors.push('Score multipliers must be greater than zero.');
    }

    return { isValid: errors.length === 0, errors };
  },

  calculate: (context) => {
    const { employees, rule, jobRoles } = context;
    if (!employees || employees.length === 0) return [];

    const allocations = (rule?.allocations || []).filter(a => a.department);
    if (allocations.length === 0) return [];

    const allocationMap = allocations.reduce((acc, a) => ({
      ...acc,
      [a.department]: parseFloat(a.scoreMultiplier) || 1.0
    }), {});

    const getEmployeeRoleScore = (emp) => {
      const dept = getEmployeeDepartment(emp, jobRoles);
      return allocationMap[dept] ?? 0;
    };

    return computeEmployeeAmounts(context, getEmployeeRoleScore);
  }
};

/**
 * Determines department for an employee based on their job role assignment.
 */
function getEmployeeDepartment(emp, jobRoles) {
  const roleId = emp.jobRoleId || emp.primaryJobRoleId;
  const role = (jobRoles || []).find(r => r.id === roleId);
  return role?.department || null;
}

// ============================================================================
// SHARED SCORE ENGINE
// Implements: Min Floor + Remainder × (roleScore × hourScore × seniorityScore)
// hourScore and seniorityScore each range from 1.0 to 2.0
// ============================================================================

/**
 * Core calculation shared by RoleScore and ByDepartment strategies.
 * @param {object} context - { troncAmount, employees, clockRecords, rule }
 * @param {function} getEmployeeRoleScore - (employee) => number (0 = excluded)
 */
function computeEmployeeAmounts(context, getEmployeeRoleScore) {
  const { troncAmount, employees, clockRecords, rule } = context;

  const floorPerEmployee =
    rule.enableMinimumFloor && rule.minimumFloorAmount > 0
      ? rule.minimumFloorAmount
      : 0;
  const totalFloor = floorPerEmployee * employees.length;
  const remainder = Math.max(0, troncAmount - totalFloor);

  const hourScores = computeHourScores(
    employees, clockRecords, rule.enableHourFactor
  );
  const seniorityScores = computeSeniorityScores(
    employees, rule.enableSeniorityFactor, rule.seniorityBands
  );

  const employeeData = employees.map(emp => {
    const roleScore = getEmployeeRoleScore(emp);
    const hourScore = hourScores[emp.id] || 1.0;
    const seniorityScore = seniorityScores[emp.id] || 1.0;
    const points = roleScore * hourScore * seniorityScore;
    return { employee: emp, roleScore, hourScore, seniorityScore, points };
  });

  const totalPoints = employeeData.reduce((sum, ed) => sum + ed.points, 0);

  const distributions = [];
  let distributed = 0;

  employeeData.forEach((ed, index) => {
    const emp = ed.employee;
    let amount;

    if (totalPoints === 0 || ed.points === 0) {
      // Unmatched employees receive only the floor guarantee
      amount = floorPerEmployee;
      distributed += amount;
    } else if (index === employeeData.length - 1) {
      amount = Math.round((troncAmount - distributed) * 100) / 100;
    } else {
      const raw = floorPerEmployee + (remainder * ed.points / totalPoints);
      amount = Math.round(raw * 100) / 100;
      distributed += amount;
    }

    distributions.push({
      employeeId: emp.id,
      employeeName: emp.name || emp.fullName || `${emp.firstName} ${emp.lastName}`,
      jobRoleName: emp.jobRoleName || emp.role || emp.jobTitle,
      amount,
      percentage: troncAmount > 0 ? (amount / troncAmount) * 100 : 0,
      calculation: {
        roleScore: ed.roleScore,
        hourScore: ed.hourScore,
        seniorityScore: ed.seniorityScore,
        points: ed.points,
        totalPoints,
      },
    });
  });

  return distributions;
}

/**
 * Returns a 1.0–2.0 score for each employee based on hours worked relative to the group.
 * Disabled: all 1.0.
 */
function computeHourScores(employees, clockRecords, enableHourFactor) {
  const result = {};
  if (!enableHourFactor) {
    employees.forEach(emp => { result[emp.id] = 1.0; });
    return result;
  }

  const empHours = {};
  employees.forEach(emp => {
    let hours = (clockRecords || [])
      .filter(c => c.employeeId === emp.id)
      .reduce((sum, c) => sum + (c.hoursWorked || 0), 0);
    if (hours === 0 && emp.contractedHoursPerWeek > 0) {
      hours = emp.contractedHoursPerWeek / 5;
    }
    empHours[emp.id] = hours || 0;
  });

  const hourValues = Object.values(empHours);
  const minH = Math.min(...hourValues);
  const maxH = Math.max(...hourValues);
  const range = maxH - minH;

  employees.forEach(emp => {
    const h = empHours[emp.id];
    result[emp.id] = range > 0 ? 1.0 + ((h - minH) / range) : 1.5;
  });
  return result;
}

/**
 * Returns a 1.0–2.0 score for each employee based on seniority bands.
 * Disabled: all 1.0.
 */
function computeSeniorityScores(employees, enableSeniorityFactor, userBands) {
  const result = {};
  if (!enableSeniorityFactor) {
    employees.forEach(emp => { result[emp.id] = 1.0; });
    return result;
  }

  const defaultBands = [
    { minMonths: 0,  maxMonths: 6,    multiplier: 1.0 },
    { minMonths: 6,  maxMonths: 12,   multiplier: 1.3 },
    { minMonths: 12, maxMonths: 24,   multiplier: 1.6 },
    { minMonths: 24, maxMonths: null, multiplier: 2.0 },
  ];
  const bands = (userBands && userBands.length > 0) ? userBands : defaultBands;

  const multValues = bands.map(b => b.multiplier);
  const minM = Math.min(...multValues);
  const maxM = Math.max(...multValues);
  const mRange = maxM - minM;

  employees.forEach(emp => {
    const months = emp.monthsOfService ?? calculateMonthsOfService(emp.startDate);
    const raw = getSeniorityMultiplier(months, bands);
    result[emp.id] = mRange > 0 ? 1.0 + ((raw - minM) / mRange) : 1.5;
  });
  return result;
}

function getSeniorityMultiplier(months, bands) {
  for (const band of bands.sort((a, b) => a.minMonths - b.minMonths)) {
if (months >= band.minMonths && (band.maxMonths === null || months < band.maxMonths)) {
   return band.multiplier;
    }
  }
  return 1.0;
}

function calculateMonthsOfService(startDate) {
  if (!startDate) return 0;
  const start = new Date(startDate);
  const now = new Date();
  return Math.max(0, (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth()));
}

// ============================================================================
// STRATEGY REGISTRY
// ============================================================================

const strategies = {
  [DistributionType.Equal]: EqualDistributionStrategy,
  [DistributionType.RoleScore]: RoleScoreDistributionStrategy,
  [DistributionType.ByDepartment]: ByDepartmentDistributionStrategy,
};

/**
 * Gets the strategy for a distribution type
 */
export function getStrategy(type) {
  const normalizedType = parseDistributionType(type);
  return strategies[normalizedType] || EqualDistributionStrategy;
}

/**
 * Parses distribution type string/number to new enum value
 */
export function parseDistributionType(type) {
  if (typeof type === 'number') {
    // Valid types: 0 (Equal), 1 (RoleScore), 2 (ByDepartment); all others are legacy
    if (type >= 0 && type <= 2) return type;
    return LEGACY_TYPE_MAP[type] ?? 0;
  }
  
  const typeStr = (type || '').toLowerCase();
  switch (typeStr) {
  case 'equal':
    return DistributionType.Equal;
    case 'rolescore':
    case 'byjobrolesco':
    case 'byjobrolescore':
      return DistributionType.RoleScore;
    case 'bydepartment':
    return DistributionType.ByDepartment;
    case 'weightedhours': // legacy — map to RoleScore
      return DistributionType.RoleScore;
    // Legacy mappings
    case 'byjobrole':
      return DistributionType.RoleScore;
    case 'hoursweighted':
    case 'byhoursandjobrole':
    case 'rolepool':
    case 'minimumfloor':
    case 'seniorityweighted':
      return DistributionType.Equal;
    default:
    return parseInt(type) || DistributionType.Equal;
  }
}

// ============================================================================
// PUBLIC API
// ============================================================================

/**
 * Validates a distribution rule
 */
export function validateRule(rule) {
  const errors = [];
  
  // Always check rule name first
  if (!rule.name || rule.name.trim() === '') {
    errors.push('Rule name is required.');
  }
  
  const ruleType = parseDistributionType(rule.type);
  const allocations = (rule.allocations || []).filter(a => a.jobRoleId);
  
  // Logic: If there are allocations provided, validate them regardless of type
  // If it's a RoleScore type (1) with NO allocations, that's automatic score-based (valid)
 // If it's a RoleScore type (1) WITH allocations, that's manual ByJobRole (validate allocations)
  
  const isRoleScoreType = (ruleType === 1 || ruleType === DistributionType.RoleScore);
  const hasAllocations = allocations.length > 0;
  
  if (isRoleScoreType && hasAllocations) {
    // Manual role allocations - validate scoreMultipliers
    const hasNonPositive = allocations.some(a => (a.scoreMultiplier || 0) <= 0);
    if (hasNonPositive) {
      errors.push('Score multipliers must be greater than zero.');
    }
    
    // Check for duplicates
    const roleIds = allocations.map(a => a.jobRoleId);
    const uniqueRoleIds = new Set(roleIds);
    if (roleIds.length !== uniqueRoleIds.size) {
      errors.push('Cannot have duplicate job role allocations.');
    }
  } else if (!isRoleScoreType) {
    // For other types (Equal, ByDepartment), use strategy validation
    const strategy = getStrategy(ruleType);
    const strategyResult = strategy.validate(rule);
    errors.push(...strategyResult.errors.filter(e => !e.toLowerCase().includes('name')));
  }
  // If isRoleScoreType and no allocations, it's automatic ByJobRoleScore - valid (no extra validation)

  // Validate universal options
  if (rule.enableMinimumFloor) {
    if (!rule.minimumFloorAmount || rule.minimumFloorAmount <= 0) {
    errors.push('Minimum floor amount must be greater than zero when enabled.');
  }
}
  if (rule.enableHourFactor) {
    // Hour factor is now a 1-2 score derived from hours worked — no weight validation needed
  }
  if (rule.enableSeniorityFactor) {
    // Seniority factor is now a 1-2 score derived from service length \u2014 no weight validation needed
  }

  return { isValid: errors.length === 0, errors };
}

/**
 * Main distribution calculation function
 */
export function calculateDistribution(rule, totalAmount, employees, jobRoles = [], clockRecords = []) {
  const strategy = getStrategy(rule.type);
  const context = {
    troncAmount: totalAmount,
    employees,
    jobRoles,
    clockRecords,
    rule
  };
  
  // All universal options (hour factor, seniority, minimum floor) are now
  // integrated directly into the score engine inside computeEmployeeAmounts.
  return strategy.calculate(context);
}

/**
 * Validates that distribution total equals the TRONC amount
 */
export function validateDistributionTotal(totalAmount, distributions) {
  const distributedTotal = distributions.reduce((sum, d) => sum + (d.amount || 0), 0);
  const difference = totalAmount - distributedTotal;
  return {
    isValid: Math.abs(difference) < 0.01,
    difference
  };
}

/**
 * Calculates auto-generated allocations for RoleScore type.
 * @deprecated RoleScore now uses manually configured percentage allocations on the rule.
 * This function is kept for backward compatibility but returns empty results.
 */
export function calculateScoreBasedAllocations(jobRoles) {
  // TroncPoints-based scoring was removed. RoleScore now uses
  // percentage allocations configured directly on the distribution rule.
  // Return empty array - UI should use rule.allocations instead.
  return [];
}

/**
 * Gets description for a distribution type
 */
export function getDistributionTypeDescription(type) {
  const strategy = getStrategy(type);
  return strategy.getDescription();
}

/**
 * Gets all distribution types with descriptions
 */
export function getAllDistributionTypes() {
  return [
    { type: 0, name: 'Equal', description: EqualDistributionStrategy.getDescription() },
    { type: 1, name: 'RoleScore', description: RoleScoreDistributionStrategy.getDescription() },
    { type: 2, name: 'ByDepartment', description: ByDepartmentDistributionStrategy.getDescription() },
  ];
}

/**
 * Gets default seniority bands
 */
export function getDefaultSeniorityBands() {
  return [
    { minMonths: 0, maxMonths: 6, multiplier: 1.0, name: 'Probationary' },
    { minMonths: 6, maxMonths: 12, multiplier: 1.2, name: 'Standard' },
    { minMonths: 12, maxMonths: 24, multiplier: 1.4, name: 'Established' },
    { minMonths: 24, maxMonths: null, multiplier: 1.6, name: 'Senior' }
  ];
}

// Legacy exports for backward compatibility
export const calculateEqual = (totalAmount, employees) => 
  EqualDistributionStrategy.calculate({ troncAmount: totalAmount, employees });

export const calculateByRoleScore = (totalAmount, employees, jobRoles) =>
  RoleScoreDistributionStrategy.calculate({ 
    troncAmount: totalAmount, 
    employees, 
    jobRoles 
  });

// Alias for old name
export const calculateByJobRoleScore = calculateByRoleScore;

// ByJobRole with manual allocations
export const calculateByJobRole = (totalAmount, employees, allocations = []) => {
  if (!allocations || allocations.length === 0) {
    return [];
  }
  
  return RoleScoreDistributionStrategy.calculate({
    troncAmount: totalAmount,
    employees,
    rule: { allocations }
  });
};

export default {
  DistributionType,
  PERCENTAGE_TOLERANCE,
  REQUIRED_TOTAL_PERCENTAGE,
  getStrategy,
  parseDistributionType,
  validateRule,
  calculateDistribution,
  validateDistributionTotal,
  calculateScoreBasedAllocations,
  getDistributionTypeDescription,
  getAllDistributionTypes,
  getDefaultSeniorityBands,
  calculateEqual,
  calculateByRoleScore,
  calculateByJobRoleScore,
  calculateByJobRole
};
